'use client'

import type {
  FiltersDataProps,
  SearchContextProps,
} from '@maestro/commerce-blocks/components/search-bar/search.types'
import type { Product, SearchResults } from '@maestro/data-layer'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { useCategoryHeaderContext } from '@maestro/commerce-blocks/providers/category-header/category-header.context'
import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:commerce-blocks:search')

const MAX_ITEMS = 10

function getSearchResultCacheKey(result: SearchResults<Product>): {
  identity: string
  content: string
} {
  const queryParamsSignature = [
    result.indexName,
    result.queryID,
    JSON.stringify(result.query?.facetFilters),
    result.query?.filters,
    result.query?.sortBy,
  ].join('|')

  const identity = result.categoryId
    ? `id:${result.categoryId}`
    : `query:${queryParamsSignature}`

  const content = [
    result.resultCount,
    result.pageCurrent,
    result.pageCount,
    result.resultsPerPage,
    result.hits?.length,
    queryParamsSignature,
  ].join('|')

  return { identity, content }
}

export const SearchContext = createContext<
  SearchContextProps & {
    filtersDisabled?: boolean
    setFiltersDisabled?: (disabled: boolean) => void
  }
>({
  searchResults: [],
  update: (data) => {
    log.debug('SearchContext update called with data:', data)
    throw new Error('Not implemented')
  },
  filtersData: undefined,
  updateFiltersData: () => {
    throw new Error('Not implemented')
  },
  currentSearchResult: undefined,
  currentSearchData: undefined,
  updateSearchData: () => {
    throw new Error('Not implemented')
  },
  clearSearchData: () => {
    throw new Error('Not implemented')
  },
  hasNoSearchResults: false,
  setHasNoSearchResults: () => {
    throw new Error('Not implemented')
  },
  filtersDisabled: false,
  setFiltersDisabled: () => {
    throw new Error('Not implemented')
  },
})

export const useSearchContext = () => useContext(SearchContext)

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const initialState: SearchResults<Product>[] = []
  const [searchResults, setSearchResults] = useState(initialState)
  const [filtersData, setFiltersData] = useState<FiltersDataProps | undefined>(
    undefined
  )
  const [currentSearchData, setCurrentSearchData] = useState<
    SearchResults<Product> | undefined
  >(undefined)
  const [hasNoSearchResults, setHasNoSearchResults] = useState(false)
  const { currentCategoryHeaderData } = useCategoryHeaderContext()
  // filtersDisabled - this is needed to prevent filters from being applied while loading
  // subcategories in the category header
  const [filtersDisabled, setFiltersDisabled] = useState(false)

  const update = useCallback((data: SearchResults<Product>): void => {
    setSearchResults((prev) => {
      const { identity: dataIdentity, content: dataContent } =
        getSearchResultCacheKey(data)
      const index = prev.findIndex((result) => {
        const { identity } = getSearchResultCacheKey(result)
        return identity === dataIdentity
      })

      if (index !== -1) {
        const current = prev[index]
        const { content: currentContent } = getSearchResultCacheKey(current)
        if (currentContent === dataContent) {
          return prev
        }

        const next = [...prev]
        next[index] = data
        return next
      }

      const next = [...prev, data]
      return next.length > MAX_ITEMS ? next.slice(-MAX_ITEMS) : next
    })
  }, [])

  const updateFiltersData = useCallback((data: FiltersDataProps) => {
    setFiltersData(data)
  }, [])

  const updateSearchData = useCallback(
    (data: SearchResults<Product> | undefined) => {
      setCurrentSearchData(data)
    },
    []
  )

  const clearSearchData = useCallback(() => {
    setCurrentSearchData(undefined)
  }, [])

  const currentSearchResult = useMemo(() => {
    if (!currentCategoryHeaderData) {
      return undefined
    }
    return searchResults.find(
      (c) => c.categoryId === currentCategoryHeaderData.mainCategoryId
    )
  }, [currentCategoryHeaderData, searchResults])

  const value = useMemo(() => {
    return {
      searchResults,
      update,
      filtersData,
      updateFiltersData,
      currentSearchResult,
      currentSearchData,
      updateSearchData,
      clearSearchData,
      hasNoSearchResults,
      setHasNoSearchResults,
      filtersDisabled,
      setFiltersDisabled,
    }
  }, [
    searchResults,
    update,
    filtersData,
    updateFiltersData,
    currentSearchResult,
    currentSearchData,
    updateSearchData,
    clearSearchData,
    hasNoSearchResults,
    setHasNoSearchResults,
    filtersDisabled,
    setFiltersDisabled,
  ])

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}
